import type { HTMLAttributes, ReactNode } from "react";
import { Table } from "../primitives/table";
import { useDesignSystem } from "./DesignSystem";
import "./surfaces.css";

export function Panel({ head, title, actions, children, className = "", ...props }: Omit<HTMLAttributes<HTMLElement>, "title"> & { head?: ReactNode; title?: ReactNode; actions?: ReactNode }) {
  return <section {...props} className={`ui-panel ${className}`.trim()}>
    {head}
    {(title || actions) && <header className="ui-panel-header">{title && <h2>{title}</h2>}{actions && <div className="ui-panel-actions">{actions}</div>}</header>}
    {children}
  </section>;
}

/** 서랍 카드의 머리띠. 레퍼런스 서랍의 잰 값 그대로다 — 키 48, 안쪽 12 16, 제목 12·500,
 *  닫기 24×24에 그림 16, 아래로 구분선 하나. 카드의 안쪽 여백을 음수 여백으로 물려 카드
 *  가장자리까지 간다. */
export function DrawerHead({ title, onClose }: { title: string; onClose?: () => void }) {
  return <header className="ui-drawer-head">
    <h2>{title}</h2>
    {onClose && <button type="button" className="ui-drawer-close" aria-label="닫기" onClick={onClose}>
      {/* claude.ai/code 의 닫기를 보고 다시 그렸다 (2026-10-16). 저쪽 아이콘은 제 글꼴
          (anthropic-sans) 안의 글리프라 꺼내 올 수가 없어서, 비율만 맞춘다 — 16 상자에
          굵기 1.5, 팔이 상자의 약 60%를 가로지른다. 전에는 24 상자에 굵기 2로 43%만 써서
          같은 자리에서 더 굵고 더 뭉툭하게 보였다. */}
      <svg viewBox="0 0 16 16" aria-hidden="true" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3.2 3.2 12.8 12.8M12.8 3.2 3.2 12.8" /></svg>
    </button>}
  </header>;
}

export function TableFrame({ label, children, className = "", tableClassName = "" }: { label: string; children: ReactNode; className?: string; tableClassName?: string }) {
  const themed = useDesignSystem();
  // The focus ring is already limited to :focus-visible, which a mouse click never
  // triggers on this region. Suppressing mousedown here instead would also block
  // focus for every input inside the table.
  if (!themed) return <div className={`ui-table-scroll ${className}`.trim()} role="region" aria-label={label} tabIndex={0}>
    <table className={`ui-table ${tableClassName}`.trim()}>{children}</table>
  </div>;
  return <Table containerProps={{ className: `ui-table-scroll ${className}`.trim(), role: "region", "aria-label": label, tabIndex: 0 }}
    className={`ui-table ${tableClassName}`.trim()}>{children}</Table>;
}

export function Feedback({ tone = "info", children }: { tone?: "info" | "error" | "success"; children: ReactNode }) {
  return <div className={`ui-feedback ui-feedback--${tone}`} role={tone === "error" ? "alert" : "status"}>{children}</div>;
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="ui-empty" role="status">{children}</p>;
}
